import { useQuery, type QueryKey } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ExecutionRunRow } from '@/features/executionRuns/components/ExecutionRunsTableV2Rows';
import { useExecutionRunsTableRowActions } from '@/features/executionRuns/components/useExecutionRunsTableRowActions';
import { useUpdateExecutionRun } from '@/features/executionRuns/hooks/useExecutionRunMutations';
import { SuiteRunEditDialog } from '@/features/executions/components/SuiteRunEditDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useSuiteRunsTableRowActions } from '@/features/executions/components/useSuiteRunsTableRowActions';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { updateSuiteRun } from '@/features/executions/data/suiteRuns';
import { updateTestRun } from '@/features/executions/data/testRuns';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableV2Footer } from '@/features/shared/components/tableSystem/TableV2Footer';
import { resolveTableV2BodyRowsHeight } from '@/features/shared/components/tableSystem/TableV2Heights';
import {
  TableV2ActionsHeaderIcon,
  tableV2ChevronColumnWidth,
  tableV2MinimumVisibleRows,
  tableV2PanelSx,
  tableV2RootSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import {
  formatTableV2DisplayedRowsLabel,
  resolveTableV2PageSize,
  resolveTableV2PageSizeOptions,
  shouldFillTableV2Rows,
  shouldShowTableV2PaginationControls,
} from '@/features/shared/components/tableSystem/TableV2QueryShellSupport';
import {
  TableV2SkeletonRow,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { buildTagColorByName } from '@/features/shared/tags';
import { suiteRunStatusOptions } from '@/shared/constants/statusOptions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { useApiErrorMessage } from '@/shared/data/errors';
import { gridPaths, queryKeys, useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Text } from '@/ui/primitives';
import { ErrorOutline, KeyboardArrowDownIcon } from '@/ui/primitives/icons';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ExecutionRunDto, QueryResult, SuiteRunDto, TestRunDto } from '@flowqa/shared';

type ExecutionRunsTableV2Props = {
  search?: string;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ExecutionRunDto>>;
  queryKey?: QueryKey;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  minimumVisibleRows?: number;
  onQueryMeta?: (meta: { total: number; items: ExecutionRunDto[] } | null) => void;
  showResultsFileName?: boolean;
  tagOptionsOverride?: TagOption[];
  active?: boolean;
  sx?: SxProps<Theme>;
};

const defaultSort: ApiSort = { field: 'startedAt', direction: 'desc' };
const skeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

type SortableExecutionRunsField = 'startedAt' | 'resultsFileCreatedAt';

const buildQueryInput = ({
  fixedFilters,
  page,
  pageSize,
  search,
  sort,
}: {
  fixedFilters?: GridQueryInput['filters'];
  page: number;
  pageSize: number;
  search?: string;
  sort: ApiSort;
}): GridQueryInput => ({
  page: page + 1,
  pageSize,
  sorts: [sort],
  filters: fixedFilters ?? [],
  search: search?.trim() ? search.trim() : undefined,
});

const sortDirectionLabel = (direction: ApiSort['direction']): string =>
  direction === 'asc' ? 'ascending' : 'descending';

const EXECUTION_RUNS_TABLE_V2_EMPTY_TEXT = 'No data';

export const ExecutionRunsTableV2 = ({
  active = true,
  fixedFilters,
  forceLoading = false,
  initialPageSize = 10,
  minimumVisibleRows = tableV2MinimumVisibleRows,
  noRowsText: _noRowsText = 'No data',
  onQueryMeta,
  pageSizeOptions = [10, 25, 50],
  queryFn,
  queryKey,
  search,
  showResultsFileName = true,
  sx,
  tagOptionsOverride,
}: ExecutionRunsTableV2Props): ReactElement => {
  const { session, loading } = useAuth();
  const toErrorMessage = useApiErrorMessage();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => resolveTableV2PageSize(initialPageSize));
  const resolvedPageSizeOptions = useMemo(
    () => resolveTableV2PageSizeOptions(pageSizeOptions),
    [pageSizeOptions],
  );
  const [sort, setSort] = useState<ApiSort>(defaultSort);
  const [expandedRunIds, setExpandedRunIds] = useState<Set<string>>(() => new Set());
  const [editTarget, setEditTarget] = useState<ExecutionRunDto | null>(null);
  const [suiteRunEditTarget, setSuiteRunEditTarget] = useState<SuiteRunDto | null>(null);
  const [testRunEditTarget, setTestRunEditTarget] = useState<TestRunDto | null>(null);
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const updateExecutionRun = useUpdateExecutionRun();
  const {
    deleteLoading,
    deleteTarget,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
  } = useExecutionRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: suiteRunDeleteLoading,
    deleteTarget: suiteRunDeleteTarget,
    deleteRowPendingId: suiteRunDeleteRowPendingId,
    onDeleteClose: onSuiteRunDeleteClose,
    onDeleteConfirm: onSuiteRunDeleteConfirm,
    onDeleteRow: onSuiteRunDeleteRow,
  } = useSuiteRunsTableRowActions({ selectedProjectId });
  const {
    deleteLoading: testRunDeleteLoading,
    deleteTarget: testRunDeleteTarget,
    deleteRowPendingId: testRunDeleteRowPendingId,
    onDeleteClose: onTestRunDeleteClose,
    onDeleteConfirm: onTestRunDeleteConfirm,
    onDeleteRow: onTestRunDeleteRow,
  } = useTestRunsTableRowActions({ selectedProjectId });
  const { groupErroredWithFailed, resolveGroupErroredWithFailed } = useProjectStatusPresentation();
  const editEditor = useDetailEditEditor<ExecutionRunDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Execution run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!editTarget) return;
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        timestamp && durationMs !== null && durationMs !== undefined
          ? new Date(timestamp.getTime() + durationMs).toISOString()
          : null;
      await updateExecutionRun.mutateAsync({
        id: editTarget.id,
        tags,
        startedAt,
        finishedAt,
        durationMs,
        status,
      });
    },
  });
  const suiteRunEditEditor = useDetailEditEditor<SuiteRunDto['status']>({
    projectId: suiteRunEditTarget?.projectId ?? null,
    successMessage: 'Suite run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!suiteRunEditTarget) return;
      await updateSuiteRun(
        { id: suiteRunEditTarget.id, tags, status, timestamp, durationMs },
        suiteRunEditTarget.organisationId ?? selectedOrganisationId ?? null,
      );
    },
  });
  const testRunEditEditor = useDetailEditEditor<TestRunDto['status']>({
    projectId: testRunEditTarget?.projectId ?? null,
    successMessage: 'Test run updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      if (!testRunEditTarget) return;
      await updateTestRun(
        { id: testRunEditTarget.id, tags, status, timestamp, durationMs },
        testRunEditTarget.organisationId ?? selectedOrganisationId ?? null,
      );
    },
  });
  const queryInput = useMemo(
    () => buildQueryInput({ fixedFilters, page, pageSize, search, sort }),
    [fixedFilters, page, pageSize, search, sort],
  );
  const queryInputKey = useMemo(() => JSON.stringify(queryInput), [queryInput]);
  const resolvedQueryKey = useMemo<QueryKey>(
    () => [
      ...(queryKey ?? queryKeys.dataGrid(gridPaths.executionRuns)),
      'v2',
      selectedOrganisationId ?? 'personal',
      selectedProjectId ?? 'all',
      queryInputKey,
    ],
    [queryInputKey, queryKey, selectedOrganisationId, selectedProjectId],
  );
  const runsQuery = useQuery({
    queryKey: resolvedQueryKey,
    enabled: session !== null && active,
    queryFn: async () => {
      if (!queryFn) throw new Error('ExecutionRunsTableV2 requires a queryFn');
      return await queryFn(queryInput);
    },
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });
  const rows = runsQuery.data?.items ?? [];
  const total = runsQuery.data?.total ?? 0;
  const editTimestamp = editTarget?.startedAt ?? editTarget?.finishedAt ?? null;
  const displayedFrom = total > 0 ? page * pageSize + 1 : 0;
  const displayedTo = total > 0 ? Math.min(total, (page + 1) * pageSize) : 0;
  const displayedRowsLabel = formatTableV2DisplayedRowsLabel({
    displayedFrom,
    displayedTo,
    entityLabel: 'execution runs',
    entitySingularLabel: 'execution run',
    pageSize,
    total,
  });
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const bodyContentRowCount = rows.length > 0 ? rows.length : 1;
  const showSkeletonRows = forceLoading || runsQuery.isLoading || runsQuery.isFetching || loading;
  const shouldFillRows = shouldFillTableV2Rows(total, minimumVisibleRows);
  const fillerRowCount =
    showSkeletonRows || runsQuery.isError || !shouldFillRows
      ? 0
      : Math.max(0, minimumVisibleRows - bodyContentRowCount);
  const fillerHeight = resolveTableV2BodyRowsHeight(fillerRowCount);
  const bodyHeight = resolveTableV2BodyRowsHeight(minimumVisibleRows);

  const goToPage = (nextPage: number): void => {
    if (nextPage < 0 || nextPage >= pageCount || nextPage === page) return;
    setPage(nextPage);
    setExpandedRunIds(new Set());
  };

  const handlePageSizeChange = (nextPageSize: number): void => {
    setPage(0);
    setPageSize(resolveTableV2PageSize(nextPageSize));
    setExpandedRunIds(new Set());
  };

  const handleSortChange = (field: SortableExecutionRunsField): void => {
    setPage(0);
    setExpandedRunIds(new Set());
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const toggleExpandedRun = (runId: string): void => {
    setExpandedRunIds((current) => {
      const next = new Set(current);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
  };

  const renderSortableHeader = (field: SortableExecutionRunsField, label: string): ReactElement => {
    const sortActive = sort.field === field;
    const direction = sortActive ? sort.direction : 'desc';
    return (
      <Box
        aria-label={
          sortActive
            ? `${label}, sorted ${sortDirectionLabel(direction)}`
            : `${label}, activate to sort descending`
        }
        component="button"
        type="button"
        onClick={() => {
          handleSortChange(field);
        }}
        sx={{
          '&:hover .ExecutionRunsTableV2-sortIcon': {
            opacity: 1,
          },
          alignItems: 'center',
          backgroundColor: 'transparent',
          border: 0,
          color: 'inherit',
          cursor: 'pointer',
          display: 'inline-flex',
          font: 'inherit',
          gap: s('sm'),
          p: s('none'),
        }}
      >
        <Box component="span">{label}</Box>
        <KeyboardArrowDownIcon
          className="ExecutionRunsTableV2-sortIcon"
          sx={{
            fontSize: (theme) => theme.app.iconSizes.md,
            opacity: sortActive ? 1 : 0,
            transform: direction === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Box>
    );
  };

  useEffect(() => {
    if (!active) return;
    setPage(0);
    setExpandedRunIds(new Set());
  }, [active, fixedFilters, search]);

  useEffect(() => {
    if (!active) return;
    onQueryMeta?.(
      runsQuery.data ? { total: runsQuery.data.total, items: runsQuery.data.items } : null,
    );
  }, [active, onQueryMeta, runsQuery.data]);

  const readinessState = showSkeletonRows
    ? 'loading'
    : runsQuery.isError
      ? 'error'
      : rows.length > 0
        ? 'ready'
        : 'empty';

  return (
    <>
      <Box sx={[tableV2PanelSx, ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}>
        <Box
          aria-hidden
          data-error={runsQuery.isError ? toErrorMessage(runsQuery.error) : undefined}
          data-state={readinessState}
          data-testid="table-v2-ready"
          sx={{
            height: 0,
            overflow: 'hidden',
            position: 'absolute',
            width: 0,
          }}
        />
        <Table aria-label="Execution runs" data-testid="table-v2-root" sx={tableV2RootSx}>
          <colgroup>
            <col width={tableV2ChevronColumnWidth} />
            <col width="23%" />
            <col width="9%" />
            <col width="15%" />
            <col width="9%" />
            <col width="14%" />
            <col width="13%" />
            <col width="13%" />
            <col width="4%" />
          </colgroup>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'background.paper' }}>
              <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
              <TableCell>Execution Run</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Pass Rate</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>{renderSortableHeader('startedAt', 'Ran At')}</TableCell>
              <TableCell>{renderSortableHeader('resultsFileCreatedAt', 'Uploaded At')}</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell sx={{ height: '52px', p: s('none'), width: s('5xl') }}>
                <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
                  <TableV2ActionsHeaderIcon />
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSkeletonRows ? (
              Array.from({ length: pageSize }, (_item, index) => (
                <TableV2SkeletonRow
                  key={`execution-runs-v2-skeleton-${String(index)}`}
                  columns={skeletonColumns}
                  isLastVisibleRow={index === pageSize - 1}
                  rowIndex={index}
                />
              ))
            ) : runsQuery.isError ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: s('xl') }}>
                  <Box sx={{ alignItems: 'center', display: 'flex', gap: s('sm') }}>
                    <ErrorOutline color="error" />
                    <Text role="body">{toErrorMessage(runsQuery.error)}</Text>
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  sx={{
                    borderBottom: '0 !important',
                    height: bodyHeight,
                    p: s('none'),
                  }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      height: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <Text role="body" color="textSecondary">
                      {EXECUTION_RUNS_TABLE_V2_EMPTY_TEXT}
                    </Text>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <ExecutionRunRow
                  key={row.id}
                  deleteRowPendingId={deleteRowPendingId}
                  editRowPendingId={editEditor.isPending ? (editTarget?.id ?? null) : null}
                  expanded={expandedRunIds.has(row.id)}
                  groupErroredWithFailed={groupErroredWithFailed}
                  isLastVisibleRow={fillerRowCount === 0 && index === rows.length - 1}
                  organisationId={selectedOrganisationId}
                  resolveGroupErroredWithFailed={resolveGroupErroredWithFailed}
                  row={row}
                  showResultsFileName={showResultsFileName}
                  suiteRunDeleteRowPendingId={suiteRunDeleteRowPendingId}
                  suiteRunEditRowPendingId={
                    suiteRunEditEditor.isPending ? (suiteRunEditTarget?.id ?? null) : null
                  }
                  tagColorByName={tagColorByName}
                  testRunDeleteRowPendingId={testRunDeleteRowPendingId}
                  testRunEditRowPendingId={
                    testRunEditEditor.isPending ? (testRunEditTarget?.id ?? null) : null
                  }
                  onDelete={onDeleteRow}
                  onDeleteSuiteRun={onSuiteRunDeleteRow}
                  onDeleteTestRun={onTestRunDeleteRow}
                  onEdit={setEditTarget}
                  onEditSuiteRun={setSuiteRunEditTarget}
                  onEditTestRun={setTestRunEditTarget}
                  onToggle={() => {
                    toggleExpandedRun(row.id);
                  }}
                />
              ))
            )}
            {fillerRowCount > 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  sx={{
                    borderBottom: '0 !important',
                    fontSize: 0,
                    height: fillerHeight,
                    lineHeight: 0,
                    p: s('none'),
                  }}
                />
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <TableV2Footer
          label={displayedRowsLabel}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={resolvedPageSizeOptions}
          showControls={shouldShowTableV2PaginationControls(total)}
          onPageChange={goToPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </Box>
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Execution Run"
        entityName={editTarget?.id ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        status={editTarget?.status}
        statusOptions={suiteRunStatusOptions}
        timestamp={editTimestamp}
        durationMs={editTarget?.durationMs ?? null}
        timestampLabel="Timestamp"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending || updateExecutionRun.isPending}
        cascadeCopy="Tags will be applied to all associated suite runs and test runs."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await editEditor.save({ tags, colors, status, timestamp, durationMs });
          setEditTarget(null);
        }}
      />
      <SuiteRunEditDialog
        open={suiteRunEditTarget !== null}
        execution={suiteRunEditTarget}
        loading={suiteRunEditEditor.isPending}
        onClose={() => {
          if (suiteRunEditEditor.isPending) return;
          setSuiteRunEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await suiteRunEditEditor.save({ tags, colors, status, timestamp, durationMs });
          setSuiteRunEditTarget(null);
        }}
      />
      <TestRunEditDialog
        open={testRunEditTarget !== null}
        execution={testRunEditTarget}
        loading={testRunEditEditor.isPending}
        onClose={() => {
          if (testRunEditEditor.isPending) return;
          setTestRunEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          await testRunEditEditor.save({ tags, colors, status, timestamp, durationMs });
          setTestRunEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'executionRun' })}
        description="This execution run and its associated suite runs and test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'executionRun',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={suiteRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'suiteRun' })}
        description="This suite run and its associated test runs will be deleted."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'suiteRun' })}
        confirmColor="error"
        loading={suiteRunDeleteLoading}
        onClose={onSuiteRunDeleteClose}
        onConfirm={onSuiteRunDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunDeleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'testRun' })}
        confirmColor="error"
        loading={testRunDeleteLoading}
        onClose={onTestRunDeleteClose}
        onConfirm={onTestRunDeleteConfirm}
      />
    </>
  );
};
